import { Popover, Transition } from '@headlessui/react'
import React, { HTMLAttributes, ReactNode, useState } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'
import { usePopper } from 'react-popper'

import { faInfoCircle } from '@fortawesome/pro-light-svg-icons/faInfoCircle'
import type { Options } from '@popperjs/core'
import clsx from 'clsx'

export interface TooltipProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'content'> {
  /* The content to render in the panel, if a string, it will render into <Tooltip /> */
  content: string | ReactNode
  /* See https://popper.js.org/docs/v2/tutorial/ for more documentation */
  popperOptions?: Partial<Options>
  /* The boolean that determines if we're displaying a custom tooltip in <Tooltip /> */
  custom?: boolean
  /* The content horizontal and vertical offsets */
  offset?: { horizontal?: number; vertical?: number }
  children?: string | ReactNode
  placement?: Options['placement']
}

/**
 * Tooltip - An element of a graphical user interface in the form of a box of text that appears when a cursor is made to hover over an item; normally used to explain the function of the item.
 */
export const Tooltip = function Tooltip({
  children,
  className,
  content,
  custom,
  offset = { horizontal: 0, vertical: 8 },
  popperOptions,
  ...props
}: TooltipProps) {
  const [open, setOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<Element>()
  const [popperElement, setPopperElement] = useState<HTMLElement>()
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null)
  const { horizontal, vertical } = offset
  const { attributes, styles } = usePopper(referenceElement, popperElement, {
    placement: props.placement || 'right',
    ...popperOptions,
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement
        }
      },
      {
        name: 'offset',
        options: {
          offset: [horizontal || 0, vertical || 8]
        }
      }
    ]
  })

  return (
    <Popover className={clsx('z-1 relative inline-block align-baseline', className)}>
      <Popover.Button
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        ref={setReferenceElement as any}
        {...props}
      >
        {children ? children : <FontAwesomeIcon className='text-primary-default ml-2' icon={faInfoCircle} />}
      </Popover.Button>

      <Transition
        className='transform transition-all'
        enter='ease-out duration-300'
        enterFrom='opacity-0 translate-y-4 sm:translate-y-0'
        enterTo='opacity-100 translate-y-0'
        leave='ease-in duration-200'
        leaveFrom='opacity-100 translate-y-0'
        leaveTo='opacity-0 translate-y-4 sm:translate-y-0'
        show={open}
      >
        <Popover.Panel>
          {custom !== true ? (
            <div
              className={clsx(
                'font-body bg-icon-default text-info-inverse pointer-events-auto fixed z-40 w-60 rounded-sm p-4 text-left text-sm font-normal normal-case shadow ring-1 ring-black hover:visible',
                className
              )}
              id='tooltip'
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              ref={setPopperElement as any}
              style={styles.popper}
              {...attributes.popper}
            >
              {content}
              <div
                data-popper-arrow
                id='arrow'
                ref={setArrowElement as any}
                style={styles.arrow}
                {...attributes.arrow}
              ></div>
            </div>
          ) : (
            content
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  )
}
